import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, AlertTriangle, ChevronLeft, ChevronRight, LogIn, ShieldOff, ShieldCheck, UserPlus, Settings } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { formatBRL } from '../../lib/currency';
import { useToast } from '../../components/ui/ToastSystem';
import { AdminPageHeader } from '../../components/admin/AdminPageHeader';

const MONTHS_PT = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

function buildIsoDate(day: string, month: string, year: string): string | null {
  if (!day || !month || !year) return null;
  return `${year}-${String(parseInt(month)).padStart(2, '0')}-${String(parseInt(day)).padStart(2, '0')}`;
}

function parseIsoToParts(iso: string | null | undefined): { day: string; month: string; year: string } {
  if (!iso) return { day: '', month: '', year: '' };
  const [y, m, d] = iso.slice(0, 10).split('-');
  return { year: y ?? '', month: m ? String(parseInt(m)) : '', day: d ? String(parseInt(d)) : '' };
}

function DateDropdowns({ day, month, year, onDay, onMonth, onYear, yearStart, yearEnd }: {
  day: string; month: string; year: string;
  onDay: (v: string) => void; onMonth: (v: string) => void; onYear: (v: string) => void;
  yearStart: number; yearEnd: number;
}) {
  const years: number[] = [];
  for (let y = yearEnd; y >= yearStart; y--) years.push(y);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1.5fr', gap: '0.5rem' }}>
      <select className="form-select" value={day} onChange={e => onDay(e.target.value)}>
        <option value="">Dia</option>
        {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={String(d)}>{d}</option>)}
      </select>
      <select className="form-select" value={month} onChange={e => onMonth(e.target.value)}>
        <option value="">Mês</option>
        {MONTHS_PT.map((m, i) => <option key={i + 1} value={String(i + 1)}>{m}</option>)}
      </select>
      <select className="form-select" value={year} onChange={e => onYear(e.target.value)}>
        <option value="">Ano</option>
        {years.map(y => <option key={y} value={String(y)}>{y}</option>)}
      </select>
    </div>
  );
}

interface UserRow {
  id: string; email: string; full_name: string; subscription_tier: string; created_at: string; banned_at?: string | null; last_login_at?: string | null; unlimited_access?: boolean | null; role?: string | null;
}
interface UserDetail {
  profile: UserRow & {
    birth_date: string | null; birth_time: string | null; birth_city: string | null;
    gender: string | null; phone: string | null; sun_sign: string | null; language: string | null;
    banned_at: string | null; ban_reason: string | null; unlimited_access: boolean | null;
    subscription_expires_at: string | null; role?: string | null;
  };
  payments: Payment[];
  credits: { questions: number; synastry: number; astromatch: number };
}
interface Payment {
  id: string; amount: number; product_price_id: string; status: string; created_at: string; credits_expected: number | null;
}
interface ActivityRow {
  feature: string; label: string; count: number; last_accessed: string; total_cost_usd: number; cache_hits: number;
}

const PAGE_SIZE = 50;
const TIER_LABELS: Record<string, string> = { free: 'Grátis', monthly: 'Mensal', annual: 'Anual', astrologer_pro: 'Astrólogo Pro' };
const TIER_COLORS: Record<string, string> = {
  free: 'var(--text-muted)', monthly: '#60a5fa', annual: '#F5C842', astrologer_pro: '#a78bfa',
};
const CREDIT_LABELS: Record<string, string> = { questions: 'Perguntas', synastry: 'Sinastria', astromatch: 'AstroMatch' };

export function AdminUsersPage() {
  const { profile: adminProfile } = useAuth();
  const { showToast } = useToast();
  const [params]              = useSearchParams();
  const [query,   setQuery]   = useState('');
  const [tierFilter, setTierFilter] = useState<string>('all');
  const [churnFilter, setChurnFilter] = useState(false);
  const [page,    setPage]    = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [users,   setUsers]   = useState<UserRow[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [results, setResults] = useState<UserRow[]>([]);
  const [detail,  setDetail]  = useState<UserDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [grantModal, setGrantModal] = useState(false);
  const [grantType, setGrantType] = useState<'questions' | 'synastry' | 'astromatch'>('questions');
  const [grantAmt,  setGrantAmt]  = useState('');
  const [grantNote, setGrantNote] = useState('');
  const [granting,  setGranting]  = useState(false);
  const [banModal,          setBanModal]          = useState(false);
  const [banReason,         setBanReason]         = useState('');
  const [banning,           setBanning]           = useState(false);
  const [impersonateModal,  setImpersonateModal]  = useState(false);
  const [impersonateReason, setImpersonateReason] = useState('');
  const [impersonating,     setImpersonating]     = useState(false);
  const [notes,     setNotes]     = useState<{ id: string; note: string; created_at: string; admin_id: string }[]>([]);
  const [newNote,   setNewNote]   = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Create user modal
  const [createModal,      setCreateModal]      = useState(false);
  const [createEmail,      setCreateEmail]      = useState('');
  const [createPwd,        setCreatePwd]        = useState('');
  const [createName,       setCreateName]       = useState('');
  const [createTier,       setCreateTier]       = useState<'free'|'monthly'|'annual'|'astrologer_pro'>('free');
  const [createUnlim,      setCreateUnlim]      = useState(false);
  const [createExpiryDay,  setCreateExpiryDay]  = useState('');
  const [createExpiryMonth,setCreateExpiryMonth]= useState('');
  const [createExpiryYear, setCreateExpiryYear] = useState('');
  const [createRole,       setCreateRole]       = useState<'user'|'admin'>('user');
  const [creating,         setCreating]         = useState(false);

  // Activity
  const [activity,        setActivity]        = useState<ActivityRow[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Set access / edit profile modal
  const [accessModal,        setAccessModal]        = useState(false);
  const [editName,           setEditName]           = useState('');
  const [editBirthDay,       setEditBirthDay]       = useState('');
  const [editBirthMonth,     setEditBirthMonth]     = useState('');
  const [editBirthYear,      setEditBirthYear]      = useState('');
  const [editBirthTime,      setEditBirthTime]      = useState('');
  const [editBirthCity,      setEditBirthCity]      = useState('');
  const [editGender,         setEditGender]         = useState('');
  const [editLanguage,       setEditLanguage]       = useState('pt');

  const [accessTier,         setAccessTier]         = useState<'free'|'monthly'|'annual'|'astrologer_pro'>('free');
  const [accessUnlim,        setAccessUnlim]        = useState(false);
  const [accessExpiryDay,    setAccessExpiryDay]    = useState('');
  const [accessExpiryMonth,  setAccessExpiryMonth]  = useState('');
  const [accessExpiryYear,   setAccessExpiryYear]   = useState('');
  const [accessRole,         setAccessRole]         = useState<'user'|'admin'>('user');
  const [savingAccess,       setSavingAccess]       = useState(false);

  const curYear = new Date().getFullYear();

  // Auto-load user from query param
  useEffect(() => {
    const id = params.get('id');
    if (id) loadDetail(id);
  }, [params]);

  // Load paginated list
  useEffect(() => { loadUsers(); }, [page, tierFilter, churnFilter]);

  async function loadUsers() {
    setLoadingList(true);

    // Select only columns guaranteed to exist in all environments.
    // banned_at / last_login_at are added by migrations that may not yet
    // be applied in production, referencing them causes a 400 and returns null.
    let q = supabase
      .from('profiles')
      .select('id, email, full_name, subscription_tier, unlimited_access, role, created_at', { count: 'exact' })
      .order('created_at', { ascending: false })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);

    if (tierFilter !== 'all') q = q.eq('subscription_tier', tierFilter);

    // churnFilter needs last_login_at which may not be available yet; skip silently
    if (churnFilter) {
      q = q.in('subscription_tier', ['monthly', 'annual']);
    }

    const { data, count, error } = await q;
    if (error) console.error('[AdminUsers] loadUsers error:', error.message);
    setUsers((data ?? []) as UserRow[]);
    setTotalCount(count ?? 0);
    setLoadingList(false);
  }

  async function search(q: string) {
    if (q.length < 2) { setResults([]); return; }
    const { data } = await supabase
      .from('profiles')
      .select('id, email, full_name, subscription_tier, unlimited_access, role, created_at')
      .or(`email.ilike.%${q}%,full_name.ilike.%${q}%`)
      .limit(10);
    setResults(data ?? []);
  }

  function handleQueryChange(v: string) {
    setQuery(v);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(v), 300);
  }

  async function loadDetail(userId: string) {
    setResults([]);
    setLoadingDetail(true);
    setActivity([]);
    const [funcResult, profileResult] = await Promise.all([
      supabase.functions.invoke('admin-get-user', { body: { userId } }),
      supabase.from('profiles').select('birth_time, birth_city, gender, language').eq('id', userId).maybeSingle(),
    ]);
    if (funcResult.data?.data) {
      const extra = profileResult.data;
      setDetail({
        ...funcResult.data.data,
        profile: {
          ...funcResult.data.data.profile,
          birth_time: extra?.birth_time ?? null,
          birth_city: extra?.birth_city ?? null,
          gender:     extra?.gender ?? null,
          phone:      null,
          language:   extra?.language ?? funcResult.data.data.profile.language ?? null,
        },
      });
    }
    setLoadingDetail(false);
    loadNotes(userId);
    loadActivity(userId);
  }

  async function loadActivity(userId: string) {
    setLoadingActivity(true);
    const { data } = await supabase.functions.invoke('admin-get-user-activity', { body: { userId } });
    if (data?.data?.activity) setActivity(data.data.activity);
    setLoadingActivity(false);
  }

  async function loadNotes(userId: string) {
    const { data } = await supabase
      .from('admin_user_notes')
      .select('id, note, created_at, admin_id')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    setNotes(data ?? []);
  }

  async function addNote() {
    if (!detail || !newNote.trim() || !adminProfile?.id) return;
    setAddingNote(true);
    await supabase.from('admin_user_notes').insert({
      user_id:  detail.profile.id,
      admin_id: adminProfile.id,
      note:     newNote.trim(),
    });
    setNewNote('');
    setAddingNote(false);
    loadNotes(detail.profile.id);
  }

  async function grantCredits() {
    if (!detail || !grantAmt || !(grantNote ?? '').trim()) return;
    setGranting(true);
    await supabase.functions.invoke('admin-grant-credits', {
      body: { userId: detail.profile.id, creditType: grantType, amount: parseInt(grantAmt), motivo: grantNote },
    });
    setGranting(false);
    setGrantModal(false);
    setGrantAmt(''); setGrantNote('');
    loadDetail(detail.profile.id);
  }

  async function deleteAccount(userId: string) {
    if (!window.confirm('Tem certeza? Esta ação é irreversível (LGPD Art. 18).')) return;
    await supabase.functions.invoke('delete-account', { body: { userId } });
    setDetail(null);
    loadUsers();
  }

  async function confirmImpersonation() {
    if (!detail || !impersonateReason.trim() || impersonateReason.trim().length < 5) return;
    setImpersonating(true);
    const { data, error: invokeError } = await supabase.functions.invoke('admin-impersonate', {
      body: { targetUserId: detail.profile.id, reason: impersonateReason.trim() },
    });
    setImpersonating(false);

    if (invokeError) {
      showToast({ type: 'error', message: `Erro de rede: ${invokeError.message}` });
      return;
    }

    if (data?.data?.token_hash) {
      // Save admin's current session so we can restore it after impersonation
      const currentSession = await supabase.auth.getSession();
      const adminToken = currentSession.data.session?.access_token ?? '';
      const adminRefresh = currentSession.data.session?.refresh_token ?? '';
      if (adminToken) {
        try {
          sessionStorage.setItem('__admin_impersonate_restore', JSON.stringify({
            access_token:  adminToken,
            refresh_token: adminRefresh,
            email: adminProfile?.email ?? '',
          }));
        } catch { /* ignore */ }
      }
      const { error: sessionError } = await supabase.auth.verifyOtp({
        token_hash: data.data.token_hash,
        type:       'magiclink',
      });
      if (!sessionError) {
        showToast({ type: 'success', message: `Impersonando ${detail.profile.email}` });
        setImpersonateModal(false);
        setImpersonateReason('');
        window.location.href = '/dashboard';
      } else {
        sessionStorage.removeItem('__admin_impersonate_restore');
        showToast({ type: 'error', message: `Erro ao iniciar sessão: ${sessionError.message}` });
      }
    } else {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao impersonar' });
    }
  }

  async function toggleUnlimited() {
    if (!detail) return;
    const newVal = !detail.profile.unlimited_access;
    const { error } = await supabase
      .from('profiles')
      .update({ unlimited_access: newVal })
      .eq('id', detail.profile.id);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao atualizar acesso ilimitado' });
    } else {
      showToast({ type: 'success', message: newVal ? 'Acesso ilimitado ativado ✦' : 'Acesso ilimitado removido' });
      loadDetail(detail.profile.id);
    }
  }

  async function banUser() {
    if (!detail || !banReason.trim()) return;
    setBanning(true);
    const { data } = await supabase.functions.invoke('admin-ban-user', {
      body: { userId: detail.profile.id, reason: banReason.trim() },
    });
    setBanning(false);
    if (data?.ok) {
      showToast({ type: 'success', message: `Usuário ${detail.profile.email} banido.` });
      setBanModal(false);
      setBanReason('');
      loadDetail(detail.profile.id);
    } else {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao banir' });
    }
  }

  async function unbanUser(userId: string) {
    if (!window.confirm('Desbanir este usuário?')) return;
    const { data } = await supabase.functions.invoke('admin-unban-user', { body: { userId } });
    if (data?.ok) {
      showToast({ type: 'success', message: 'Usuário desbanido.' });
      loadDetail(userId);
    } else {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao desbanir' });
    }
  }

  async function createUser() {
    if (!createEmail.trim() || createPwd.length < 6) return;
    setCreating(true);
    const { data, error } = await supabase.functions.invoke('admin-create-user', {
      body: {
        email:            createEmail.trim(),
        password:         createPwd,
        fullName:         createName.trim() || undefined,
        subscriptionTier: createTier,
        unlimitedAccess:  createUnlim,
        expiresAt:        buildIsoDate(createExpiryDay, createExpiryMonth, createExpiryYear),
        role:             createRole,
      },
    });
    setCreating(false);
    if (error || data?.error) {
      showToast({ type: 'error', message: data?.error ?? 'Erro ao criar usuário' });
    } else {
      showToast({ type: 'success', message: `Usuário ${createEmail} criado com sucesso ✦` });
      setCreateModal(false);
      setCreateEmail(''); setCreatePwd(''); setCreateName('');
      setCreateTier('free'); setCreateUnlim(false);
      setCreateExpiryDay(''); setCreateExpiryMonth(''); setCreateExpiryYear('');
      setCreateRole('user');
      loadUsers();
    }
  }

  function openAccessModal() {
    if (!detail) return;
    setEditName(detail.profile.full_name ?? '');
    const birthParts = parseIsoToParts(detail.profile.birth_date);
    setEditBirthDay(birthParts.day);
    setEditBirthMonth(birthParts.month);
    setEditBirthYear(birthParts.year);
    setEditBirthTime(detail.profile.birth_time ?? '');
    setEditBirthCity(detail.profile.birth_city ?? '');
    setEditGender(detail.profile.gender ?? '');
    setEditLanguage(detail.profile.language ?? 'pt-BR');

    setAccessTier((detail.profile.subscription_tier as 'free'|'monthly'|'annual'|'astrologer_pro') ?? 'free');
    setAccessUnlim(detail.profile.unlimited_access ?? false);
    const expiryParts = parseIsoToParts(detail.profile.subscription_expires_at);
    setAccessExpiryDay(expiryParts.day);
    setAccessExpiryMonth(expiryParts.month);
    setAccessExpiryYear(expiryParts.year);
    setAccessRole((detail.profile.role as 'user'|'admin') ?? 'user');
    setAccessModal(true);
  }

  async function saveAccess() {
    if (!detail) return;
    setSavingAccess(true);
    const expiryIso = buildIsoDate(accessExpiryDay, accessExpiryMonth, accessExpiryYear);
    const { error } = await supabase.from('profiles').update({
      full_name:               editName.trim() || null,
      birth_date:              buildIsoDate(editBirthDay, editBirthMonth, editBirthYear),
      birth_time:              editBirthTime.trim() || null,
      birth_city:              editBirthCity.trim() || null,
      gender:                  editGender || null,
      language:                editLanguage || null,
      subscription_tier:       accessTier,
      unlimited_access:        accessUnlim,
      subscription_expires_at: expiryIso ? expiryIso + 'T00:00:00.000Z' : null,
      role:                    accessRole,
      updated_at:              new Date().toISOString(),
    }).eq('id', detail.profile.id);
    setSavingAccess(false);
    if (error) {
      showToast({ type: 'error', message: 'Erro ao atualizar usuário' });
    } else {
      showToast({ type: 'success', message: 'Usuário atualizado ✦' });
      setAccessModal(false);
      loadDetail(detail.profile.id);
    }
  }

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const sectionLabel: React.CSSProperties = {
    fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.06em', margin: '1rem 0 0.75rem',
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ flex: 1 }}>
          <AdminPageHeader
            title="Usuários"
            objective="Gerencie todos os usuários da plataforma: visualize histórico de uso, altere planos, conceda créditos extras, resolva problemas de acesso e tome ações administrativas com registro automático de auditoria."
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingTop: '0.2rem' }}>
          <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{totalCount.toLocaleString('pt-BR')} usuários</span>
          <button className="btn btn-primary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={() => setCreateModal(true)}>
            <UserPlus size={14} /> Criar usuário
          </button>
        </div>
      </div>

      {/* Search + filter */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 280px', maxWidth: '420px' }}>
          <Search size={15} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input type="text" className="form-input" value={query} onChange={e => handleQueryChange(e.target.value)}
            placeholder="Buscar por e-mail ou nome..." style={{ paddingLeft: '2.25rem' }} />
          {results.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)', marginTop: '4px', boxShadow: '0 8px 24px rgba(0,0,0,0.3)' }}>
              {results.map(r => (
                <button key={r.id} onClick={() => { setQuery(r.email ?? r.full_name ?? ''); loadDetail(r.id); }}
                  style={{ width: '100%', padding: '0.75rem 1rem', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', borderBottom: '1px solid var(--border-subtle)' }}>
                  <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.1rem', fontSize: 'var(--text-label)' }}>{r.full_name ?? '-'}</p>
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{r.email} · <span style={{ color: TIER_COLORS[r.subscription_tier] }}>{TIER_LABELS[r.subscription_tier] ?? r.subscription_tier}</span></p>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Plan filter */}
        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
          {[['all', 'Todos'], ['free', 'Grátis'], ['monthly', 'Mensal'], ['annual', 'Anual'], ['astrologer_pro', 'Astrólogo Pro']].map(([key, label]) => (
            <button key={key} onClick={() => { setTierFilter(key); setPage(0); }}
              className={tierFilter === key ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}>
              {label}
            </button>
          ))}
          <button
            onClick={() => { setChurnFilter(!churnFilter); setPage(0); }}
            className={churnFilter ? 'btn btn-primary btn-sm' : 'btn btn-secondary btn-sm'}
            title="Usuários com plano pago que não acessam há 30+ dias, alto risco de cancelamento"
            style={{ borderColor: churnFilter ? '#f87171' : undefined, background: churnFilter ? 'rgba(248,113,113,0.15)' : undefined }}>
            ⚠ Risco Churn
          </button>
        </div>
      </div>

      {/* User list */}
      {!detail && (
        <>
          {loadingList ? (
            <div className="card shimmer" style={{ height: '300px' }} />
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '1rem' }}>
              <div style={{ overflowX: 'auto' }}>
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Nome</th><th>E-mail</th><th>Plano</th><th>Acesso</th><th>Idioma</th><th>Desde</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => loadDetail(u.id)}>
                      <td style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>{u.full_name ?? '-'}</td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{u.email}</td>
                      <td>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px',
                          backgroundColor: u.subscription_tier === 'annual' ? 'rgba(245,200,66,0.12)' : u.subscription_tier === 'monthly' ? 'rgba(96,165,250,0.12)' : 'rgba(255,255,255,0.05)',
                          color: TIER_COLORS[u.subscription_tier] ?? 'var(--text-muted)' }}>
                          {TIER_LABELS[u.subscription_tier] ?? u.subscription_tier}
                        </span>
                      </td>
                      <td>
                        {u.unlimited_access && (
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '999px', backgroundColor: 'rgba(31,78,95,0.15)', color: 'var(--petrol)', fontWeight: 700 }}>∞ Ilimitado</span>
                        )}
                      </td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>-</td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{new Date(u.created_at).toLocaleDateString('pt-BR')}</td>
                      <td style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)' }}>Ver →</td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '2rem' }}>Nenhum usuário encontrado</td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                <ChevronLeft size={15} />
              </button>
              <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>
                {page + 1} / {totalPages}
              </span>
              <button className="btn btn-ghost btn-sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                <ChevronRight size={15} />
              </button>
            </div>
          )}
        </>
      )}

      {loadingDetail && <div className="card shimmer" style={{ height: '300px' }} />}

      {detail && !loadingDetail && (
        <div className="animate-fade-in">
          <button className="btn btn-ghost btn-sm" style={{ marginBottom: '1rem' }} onClick={() => { setDetail(null); setQuery(''); }}>
            ← Voltar à lista
          </button>

          {/* Profile header */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div>
                <p className="font-display" style={{ fontSize: '1.25rem', color: 'var(--text-primary)', margin: '0 0 0.25rem' }}>{detail.profile.full_name ?? 'Sem nome'}</p>
                <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>{detail.profile.email}</p>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.25rem 0 0' }}>
                  <span style={{ color: TIER_COLORS[detail.profile.subscription_tier] }}>{TIER_LABELS[detail.profile.subscription_tier]}</span>
                  {' · '}{detail.profile.sun_sign ?? 'Signo não informado'}
                  {detail.profile.birth_date && ` · Nasc. ${new Date(detail.profile.birth_date + 'T00:00:00').toLocaleDateString('pt-BR')}`}
                  {detail.profile.subscription_expires_at && ` · Expira ${new Date(detail.profile.subscription_expires_at).toLocaleDateString('pt-BR')}`}
                  {' · membro desde '}{new Date(detail.profile.created_at).toLocaleDateString('pt-BR')}
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.2rem 0 0', fontFamily: 'monospace' }}>
                  ID: {detail.profile.id}
                </p>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: '0.2rem 0 0' }}>
                  Último acesso: -
                </p>
                {detail.profile.banned_at && (
                  <p style={{ color: '#f87171', fontSize: 'var(--text-caption)', margin: '0.35rem 0 0', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    <ShieldOff size={12} /> Banido em {new Date(detail.profile.banned_at).toLocaleDateString('pt-BR')}
                    {detail.profile.ban_reason ? ` · Motivo: ${detail.profile.ban_reason}` : ''}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}
                  onClick={() => { setImpersonateModal(true); setImpersonateReason(''); }}
                  aria-label="Impersonar este usuário"
                  title="Entrar como este usuário">
                  <LogIn size={14} /> Impersonar
                </button>
                <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }} onClick={openAccessModal}>
                  <Settings size={14} /> Editar
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => setGrantModal(true)}>+ Créditos</button>
                <button
                  className="btn btn-secondary btn-sm"
                  style={{ color: detail.profile.unlimited_access ? '#4ade80' : 'var(--text-secondary)' }}
                  onClick={toggleUnlimited}
                  title={detail.profile.unlimited_access ? 'Remover acesso ilimitado' : 'Ativar acesso ilimitado'}
                >
                  {detail.profile.unlimited_access ? '∞ Ilimitado' : '∞ Limitar'}
                </button>
                {detail.profile.banned_at ? (
                  <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#4ade80' }}
                    onClick={() => unbanUser(detail.profile.id)}>
                    <ShieldCheck size={14} /> Desbanir
                  </button>
                ) : (
                  <button className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#fbbf24' }}
                    onClick={() => setBanModal(true)}>
                    <ShieldOff size={14} /> Banir
                  </button>
                )}
                <button className="btn btn-danger btn-sm" onClick={() => deleteAccount(detail.profile.id)}>Excluir conta</button>
              </div>
            </div>
          </div>

          {/* Credits */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Créditos</p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {Object.entries(detail.credits).map(([type, bal]) => (
                <div key={type} style={{ textAlign: 'center', flex: '1 1 80px' }}>
                  <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--petrol)', fontFamily: 'var(--font-display)', margin: '0 0 0.2rem' }}>{bal}</p>
                  <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>{CREDIT_LABELS[type] ?? type}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Services accessed */}
          <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '1rem' }}>
            <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}
                title="Os serviços abaixo são registrados a cada uso. Dados aparecem após o próximo acesso do usuário.">
                Serviços acessados
              </p>
            </div>
            {loadingActivity ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>Carregando...</div>
            ) : activity.length === 0 ? (
              <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', fontSize: 'var(--text-caption)' }}>
                Nenhum serviço registrado ainda. Isso pode ocorrer se o usuário acessou serviços antes do sistema de registro ser ativado.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th>Serviço</th>
                      <th style={{ textAlign: 'right' }}>Acessos</th>
                      <th style={{ textAlign: 'right' }}>Cache hits</th>
                      <th style={{ textAlign: 'right' }}>Custo (USD)</th>
                      <th>Último acesso</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.map(a => (
                      <tr key={a.feature}>
                        <td style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>
                          {a.label || a.feature}
                          {a.label && a.label !== a.feature && (
                            <span style={{ display: 'block', fontSize: '10px', color: 'var(--text-muted)', fontWeight: 400, fontFamily: 'monospace' }}>{a.feature}</span>
                          )}
                        </td>
                        <td style={{ textAlign: 'right', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>{a.count}</td>
                        <td style={{ textAlign: 'right', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>{a.cache_hits}</td>
                        <td style={{ textAlign: 'right', color: a.total_cost_usd > 0 ? '#fbbf24' : 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
                          {a.total_cost_usd > 0 ? `$${a.total_cost_usd.toFixed(4)}` : '-'}
                        </td>
                        <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                          {new Date(a.last_accessed).toLocaleString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Internal Notes */}
          <div className="card" style={{ marginBottom: '1rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.75rem' }}>Notas internas de suporte</p>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <input type="text" className="form-input" value={newNote} onChange={e => setNewNote(e.target.value)}
                placeholder="Adicionar nota interna..." style={{ flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }} />
              <button className="btn btn-primary btn-sm" onClick={addNote} disabled={addingNote || newNote.trim().length < 2}>
                {addingNote ? '...' : 'Adicionar'}
              </button>
            </div>
            {notes.length === 0 ? (
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', fontStyle: 'italic' }}>Nenhuma nota ainda.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {notes.map(n => (
                  <div key={n.id} style={{ padding: '0.6rem 0.75rem', backgroundColor: 'var(--bg-base)', borderRadius: 'var(--radius-sm)', borderLeft: '2px solid var(--petrol)' }}>
                    <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-label)', color: 'var(--text-primary)' }}>{n.note}</p>
                    <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-muted)' }}>{new Date(n.created_at).toLocaleString('pt-BR')}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Payments */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-subtle)' }}>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 }}>Pagamentos</p>
            </div>
            <table className="data-table" style={{ width: '100%' }}>
              <thead><tr><th>Data</th><th>Produto</th><th>Valor</th><th>Status</th><th>Créditos</th></tr></thead>
              <tbody>
                {detail.payments.map(p => (
                  <tr key={p.id}>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{new Date(p.created_at).toLocaleDateString('pt-BR')}</td>
                    <td style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' }}>{p.product_price_id}</td>
                    <td style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{formatBRL(p.amount)}</td>
                    <td>
                      <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '999px',
                        backgroundColor: p.status === 'succeeded' ? 'rgba(74,222,128,0.12)' : 'rgba(239,68,68,0.1)',
                        color: p.status === 'succeeded' ? '#4ade80' : '#fca5a5' }}>
                        {p.status}
                      </span>
                    </td>
                    <td>
                      {p.credits_expected !== null && p.status === 'succeeded' ? (
                        <span style={{ fontSize: '11px', color: '#4ade80' }}>✓ Concedido</span>
                      ) : p.credits_expected === null ? (
                        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>-</span>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <AlertTriangle size={12} color="#fbbf24" />
                          <span style={{ fontSize: '11px', color: '#fbbf24' }}>Não concedido</span>
                          <button className="btn btn-secondary btn-sm" style={{ fontSize: '10px', padding: '1px 6px' }} onClick={() => setGrantModal(true)}>Conceder</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {detail.payments.length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', fontStyle: 'italic', padding: '1.5rem' }}>Nenhum pagamento</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Ban user modal */}
      {banModal && detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '380px', width: '100%', border: '1px solid rgba(239,68,68,0.3)' }}>
            <h2 style={{ fontSize: '1rem', color: '#f87171', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <ShieldOff size={16} /> Banir usuário
            </h2>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
              O usuário <strong>{detail.profile.email}</strong> será desconectado imediatamente e não poderá fazer login.
            </p>
            <div className="form-group">
              <label className="form-label">Motivo do banimento (obrigatório)</label>
              <textarea className="form-input" value={banReason} onChange={e => setBanReason(e.target.value)}
                placeholder="Ex: Violação dos termos de uso, spam/fraude"
                rows={3} style={{ resize: 'vertical', minHeight: '80px' }} />
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginTop: '0.35rem', lineHeight: 1.5 }}>
                O motivo do banimento é registrado e pode ser solicitado em processos legais (LGPD).
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-danger btn-sm" onClick={banUser} disabled={banning || banReason.trim().length < 5}>
                {banning ? 'Banindo...' : 'Confirmar banimento'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setBanModal(false); setBanReason(''); }}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Impersonation reason modal */}
      {impersonateModal && detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '400px', width: '100%', border: '1px solid rgba(245,200,66,0.3)' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--petrol)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <LogIn size={16} /> Impersonar usuário
            </h2>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', marginBottom: '1rem', lineHeight: 1.6 }}>
              Você irá entrar como <strong>{detail.profile.email}</strong>. Esta ação é registrada em auditoria.
              O motivo é obrigatório para fins de conformidade.
            </p>
            <div className="form-group">
              <label className="form-label">Motivo (obrigatório, mín. 5 caracteres)</label>
              <textarea
                className="form-input"
                value={impersonateReason}
                onChange={e => setImpersonateReason(e.target.value)}
                placeholder="Ex: Suporte ao cliente, usuário relatou problema com pagamento"
                rows={3}
                style={{ resize: 'vertical', minHeight: '80px' }}
                maxLength={500}
              />
              <p className="form-hint" style={{ textAlign: 'right' }}>{impersonateReason.length}/500</p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={confirmImpersonation}
                disabled={impersonating || impersonateReason.trim().length < 5}
              >
                {impersonating ? 'Iniciando sessão...' : 'Confirmar impersonação'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => { setImpersonateModal(false); setImpersonateReason(''); }}>
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create user modal */}
      {createModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '420px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <UserPlus size={16} /> Criar usuário
            </h2>
            <div className="form-group">
              <label className="form-label">E-mail *</label>
              <input type="email" className="form-input" value={createEmail} onChange={e => setCreateEmail(e.target.value)} placeholder="usuario@email.com" />
            </div>
            <div className="form-group">
              <label className="form-label">Senha * (mín. 6 caracteres)</label>
              <input type="password" className="form-input" value={createPwd} onChange={e => setCreatePwd(e.target.value)} placeholder="••••••••" />
            </div>
            <div className="form-group">
              <label className="form-label">Nome completo</label>
              <input type="text" className="form-input" value={createName} onChange={e => setCreateName(e.target.value)} placeholder="Nome do usuário" />
            </div>
            <div className="form-group">
              <label className="form-label">Plano de acesso</label>
              <select className="form-select" value={createTier} onChange={e => setCreateTier(e.target.value as typeof createTier)}>
                <option value="free">Grátis</option>
                <option value="monthly">Mensal</option>
                <option value="annual">Anual</option>
                <option value="astrologer_pro">Astrólogo Pro</option>
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="createUnlim" checked={createUnlim} onChange={e => setCreateUnlim(e.target.checked)} />
              <label htmlFor="createUnlim" className="form-label" style={{ margin: 0 }}>Acesso ilimitado ∞</label>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="createAdmin" checked={createRole === 'admin'} onChange={e => setCreateRole(e.target.checked ? 'admin' : 'user')} />
              <label htmlFor="createAdmin" className="form-label" style={{ margin: 0 }}>Acesso admin</label>
            </div>
            <div className="form-group">
              <label className="form-label">Data de expiração (opcional)</label>
              <DateDropdowns
                day={createExpiryDay} month={createExpiryMonth} year={createExpiryYear}
                onDay={setCreateExpiryDay} onMonth={setCreateExpiryMonth} onYear={setCreateExpiryYear}
                yearStart={curYear} yearEnd={curYear + 10}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={createUser} disabled={creating || !createEmail.trim() || createPwd.length < 6}>
                {creating ? 'Criando...' : 'Criar usuário'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setCreateModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit user / set access modal */}
      {accessModal && detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '520px', width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <Settings size={16} /> Editar usuário
            </h2>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>{detail.profile.email}</p>

            <p style={sectionLabel}>Perfil</p>

            <div className="form-group">
              <label className="form-label">Nome completo</label>
              <input type="text" className="form-input" value={editName} onChange={e => setEditName(e.target.value)} placeholder="Nome do usuário" />
            </div>
            <div className="form-group">
              <label className="form-label">Data de nascimento</label>
              <DateDropdowns
                day={editBirthDay} month={editBirthMonth} year={editBirthYear}
                onDay={setEditBirthDay} onMonth={setEditBirthMonth} onYear={setEditBirthYear}
                yearStart={1920} yearEnd={curYear}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Horário de nascimento (opcional)</label>
              <input type="time" className="form-input" value={editBirthTime} onChange={e => setEditBirthTime(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Cidade de nascimento</label>
              <input type="text" className="form-input" value={editBirthCity} onChange={e => setEditBirthCity(e.target.value)} placeholder="Ex: São Paulo, SP" />
            </div>
            <div className="form-group">
              <label className="form-label">Gênero</label>
              <select className="form-select" value={editGender} onChange={e => setEditGender(e.target.value)}>
                <option value="">Não informado</option>
                <option value="F">Feminino</option>
                <option value="M">Masculino</option>
                <option value="NB">Não-binário</option>
                <option value="prefer_not_to_say">Prefiro não dizer</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Idioma</label>
              <select className="form-select" value={editLanguage} onChange={e => setEditLanguage(e.target.value)}>
                <option value="pt-BR">Português (Brasil)</option>
                <option value="pt-PT">Português (Portugal)</option>
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
              </select>
            </div>
            <p style={sectionLabel}>Acesso</p>

            <div className="form-group">
              <label className="form-label">Plano</label>
              <select className="form-select" value={accessTier} onChange={e => setAccessTier(e.target.value as typeof accessTier)}>
                <option value="free">Grátis</option>
                <option value="monthly">Mensal</option>
                <option value="annual">Anual</option>
                <option value="astrologer_pro">Astrólogo Pro</option>
              </select>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="accessUnlim" checked={accessUnlim} onChange={e => setAccessUnlim(e.target.checked)} />
              <label htmlFor="accessUnlim" className="form-label" style={{ margin: 0 }}>Acesso ilimitado ∞</label>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input type="checkbox" id="accessAdmin" checked={accessRole === 'admin'} onChange={e => setAccessRole(e.target.checked ? 'admin' : 'user')} />
              <label htmlFor="accessAdmin" className="form-label" style={{ margin: 0 }}>Acesso admin</label>
            </div>
            <div className="form-group">
              <label className="form-label">Data de expiração (opcional)</label>
              <DateDropdowns
                day={accessExpiryDay} month={accessExpiryMonth} year={accessExpiryYear}
                onDay={setAccessExpiryDay} onMonth={setAccessExpiryMonth} onYear={setAccessExpiryYear}
                yearStart={curYear} yearEnd={curYear + 10}
              />
              <p className="form-hint">Deixe em branco para não expirar.</p>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={saveAccess} disabled={savingAccess}>
                {savingAccess ? 'Salvando...' : 'Salvar'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAccessModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* Grant credits modal */}
      {grantModal && detail && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '380px', width: '100%' }}>
            <h2 style={{ fontSize: '1rem', color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Conceder créditos</h2>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
              Créditos concedidos manualmente são registrados em auditoria e exibidos no histórico do usuário.
            </p>
            <div className="form-group">
              <label className="form-label">Tipo</label>
              <select className="form-select" value={grantType} onChange={e => setGrantType(e.target.value as typeof grantType)}>
                <option value="questions">Perguntas</option>
                <option value="synastry">Sinastria</option>
                <option value="astromatch">AstroMatch</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Quantidade</label>
              <input type="number" className="form-input" value={grantAmt} onChange={e => setGrantAmt(e.target.value)} min="1" max="100" />
            </div>
            <div className="form-group">
              <label className="form-label">Motivo (obrigatório)</label>
              <input type="text" className="form-input" value={grantNote} onChange={e => setGrantNote(e.target.value)} placeholder="Ex: Suporte, erro no pagamento" />
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button className="btn btn-primary btn-sm" onClick={grantCredits} disabled={granting || !grantAmt || grantNote.length < 10}>
                {granting ? 'Concedendo...' : 'Conceder'}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setGrantModal(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
